import { Card, CardHeader } from '@wordpress/components';
import { Fragment, useState, useMemo, RawHTML } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { seen } from '@wordpress/icons';
import { useEntityRecords } from '@wordpress/core-data';
import { DataViews, filterSortAndPaginate } from '@wordpress/dataviews/wp';
import { decodeEntities } from '@wordpress/html-entities';
import { deletePost } from '@wordpress/fields';
import { dateI18n, getDate, getSettings } from '@wordpress/date';

const defaultLayouts = {
	table: {
		layout: {
			primaryField: 'id',
		},
	},
};

const Logs = () => {
	const [ view, setView ] = useState( {
		type: 'table',
		page: 1,
		perPage: 10,
		sort: {
			field: 'date',
			direction: 'desc',
		},
		search: '',
		filters: [],
		titleField: 'title',
		fields: [ 'id', 'popup-id', 'content', 'meta', 'type', 'date' ],
		layout: defaultLayouts.table.layout,
	} );

	const { records: logs, isResolving: isLoadingLogs } = useEntityRecords(
		'postType',
		'popper_log',
		{ per_page: -1, _embed: 'wp:term' }
	);

	const fields = useMemo(
		() => [
			{
				id: 'id',
				label: __( 'ID' ),
				enableGlobalSearch: true,
			},
			{
				id: 'popup-id',
				label: __( 'Popup ID' ),
				getValue: ( { item } ) => item.parent,
				enableGlobalSearch: true,
			},
			{
				header: __( 'Title' ),
				id: 'title',
				getValue: ( { item } ) => item.title.rendered,
				render: ( { item } ) => {
					return decodeEntities(
						item.title?.rendered || 'Log #' + item.id
					);
				},
				enableGlobalSearch: true,
				enableHiding: false,
				enableSorting: true,
			},
			{
				header: __( 'Content' ),
				id: 'content',
				getValue: ( { item } ) => item.content.raw,
				render: ( { item } ) => {
					return (
						<RawHTML className="field-content">
							{ decodeEntities( item.content.raw ) }
						</RawHTML>
					);
				},
				enableHiding: false,
				enableSorting: false,
				maxWidth: '240px',
				width: '240px',
			},
			{
				header: __( 'Meta' ),
				id: 'meta',
				getValue: ( { item } ) => item.content.raw,
				render: ( { item } ) => {
					return (
						<RawHTML className="field-content">
							{ decodeEntities( item.meta._popper_log_meta ) }
						</RawHTML>
					);
				},
				enableHiding: false,
				enableSorting: false,
			},
			{
				header: __( 'Type' ),
				id: 'type',
				getValue: ( { item } ) => item._embedded[ 'wp:term' ],
				render: ( { item } ) => {
					return item._embedded[ 'wp:term' ][ 0 ].map(
						( a ) => a.name
					);
				},
				enableHiding: false,
				enableSorting: false,
				maxWidth: '240px',
				width: '240px',
			},
			{
				header: __( 'Date' ),
				id: 'date',
				render: ( { item } ) => {
					const formattedDate = dateI18n(
						getSettings().formats.datetimeAbbreviated,
						getDate( item.date )
					);
					return <time>{ formattedDate }</time>;
				},
			},
		],
		[]
	);

	const { data: processedData, paginationInfo } = useMemo( () => {
		return filterSortAndPaginate( logs, view, fields );
	}, [ view, logs, fields ] );

	const { isEligible, ...deleteAction } = deletePost;

	const actions = [
		{
			id: 'view',
			label: __( 'View item', 'popper' ),
			isPrimary: true,
			icon: seen,
			supportsBulk: false,
			modalSize: 'large',
			RenderModal: ( { items } ) => {
				const item = items[ 0 ];
				return (
					<Fragment>
						<h2>{ item.title.raw }</h2>
						<small>Popup id: { item.parent }</small>
						<p>{ decodeEntities( item.content.raw ) }</p>
						<pre>
							{ decodeEntities( item.meta._popper_log_meta ) }
						</pre>
					</Fragment>
				);
			},
		},
		deleteAction,
	];

	return (
		<Fragment>
			<Card>
				<CardHeader size="small">
					<b>{ __( 'Logs', 'popper' ) }</b>
				</CardHeader>

				<div>
					<DataViews
						paginationInfo={ paginationInfo }
						fields={ fields }
						actions={ actions }
						data={ processedData }
						isLoading={ isLoadingLogs }
						view={ view }
						onChangeView={ setView }
						defaultLayouts={ defaultLayouts }
					/>
				</div>
			</Card>
		</Fragment>
	);
};

export default Logs;
